import React from "react";
import { MapPin, Clock, Phone, ArrowUpRight } from "lucide-react";
import { Button } from "../ui/button";
import Reveal from "../Reveal";
import { studio } from "../../mock";

const Row = ({ icon: Icon, label, children, testId }) => (
  <div className="flex gap-5 border-b border-border py-7 last:border-b-0" data-testid={testId}>
    <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center border border-border text-primary">
      <Icon className="h-4 w-4" />
    </span>
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-muted-foreground">{label}</p>
      <div className="mt-2 text-lg font-medium leading-snug md:text-xl">{children}</div>
    </div>
  </div>
);

const LocationSection = () => {
  return (
    <section className="relative py-24 lg:py-32" id="location" data-testid="location-section">
      <div className="container-x">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="eyebrow">Find us</p>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="display mt-6 text-[clamp(3.5rem,8vw,8rem)] uppercase leading-[0.85] tracking-wide">
                Location &amp;
                <br />
                Contact
              </h2>
            </Reveal>

            <Reveal delay={160} className="mt-10">
              <Row icon={MapPin} label="Visit us at" testId="location-address">
                {studio.address.line1}
                <br />
                <span className="text-muted-foreground">
                  {studio.address.city}, {studio.address.province}
                </span>
              </Row>
              <Row icon={Clock} label="Hours" testId="location-hours">
                {studio.hours.label}
                <br />
                <span className="text-muted-foreground">
                  {studio.hours.open} – {studio.hours.close}
                </span>
              </Row>
              <Row icon={Phone} label="Call for details" testId="location-phone">
                <a href={studio.phoneHref} className="link-underline transition-colors hover:text-primary">
                  {studio.phone}
                </a>
              </Row>
            </Reveal>

            <Reveal delay={240}>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="mt-10 h-12 rounded-none border-foreground/30 bg-transparent px-7 text-[11px] font-semibold uppercase tracking-[0.2em] hover:border-foreground hover:bg-foreground hover:text-background"
                data-testid="location-directions"
              >
                <a href={studio.mapsLink} target="_blank" rel="noreferrer">
                  Get Directions <ArrowUpRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </Reveal>
          </div>

          <Reveal delay={120} className="lg:col-span-7">
            <div className="relative h-full min-h-[420px] overflow-hidden border border-border bg-card lg:min-h-[560px]">
              <iframe
                title="Avalon Tattoo Collective on Google Maps"
                src={studio.mapsEmbed}
                className="absolute inset-0 h-full w-full grayscale contrast-[1.05] invert-[0.92] hue-rotate-180 transition-[filter] duration-700 hover:grayscale-0 hover:invert-0 hover:hue-rotate-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                data-testid="location-map"
              />
              <div className="pointer-events-none absolute bottom-5 left-5 border border-border bg-background/90 px-4 py-3 backdrop-blur">
                <p className="display text-2xl leading-none">Avalon</p>
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">{studio.address.line1}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default LocationSection;
